<template>
  <div ref="baseTable" class="base-table">
    <div
      ref="baseTableHeader"
      class="base-table__header"
      :class="tableHeaderDirection"
    >
      <div v-if="$slots.title || title" class="base-table__header-title">
        <slot v-if="$slots.title" name="title" />
        <template v-else>{{ title }}</template>
      </div>
      <div class="base-table__header-operate">
        <slot name="operate" />
      </div>
    </div>
    <el-table
      :id="tableId"
      ref="elTableRef"
      :key="tableKey"
      v-loading="loading"
      :data="tableList"
      :row-key="rowKey"
      style="width: 100%"
      :height="height || tableHeight"
      :header-cell-style="{
        height: '32px',
        'line-height': '32px',
        'font-size': '14px',
        color: '#8c95b0',
      }"
      :row-style="{
        height: '32px',
        'line-height': '32px',
        'font-size': '14px',
        color: '#8c95b0',
      }"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <template #empty>
        <div v-if="loading" />
        <base-empty v-if="!loading && showEmpty" description="暂无数据" />
      </template>
      <el-table-column
        v-if="showSelectColumn"
        type="selection"
        align="center"
        width="60"
        fixed
      />
      <el-table-column
        v-if="showIndexColumn"
        type="index"
        label="序号"
        align="center"
        width="60"
        :index="customRenderIndex"
        fixed
      />
      <el-table-column
        v-for="column in columnList"
        :key="column.label"
        :column-key="column.prop"
        v-bind="{
          align: 'center',
          'header-align': 'center',
          'show-overflow-tooltip': true,
          ...column,
        }"
      >
        <template #default="scope">
          <slot
            :name="column.customRender"
            :row="scope.row"
            :index="scope.$index"
            :column="column"
          >
            {{ doTranslate(scope.row[column.prop], column.translate) }}
          </slot>
        </template>

        <template #header="scope">
          <slot :name="column.customHeader" :column="column" :index="scope.$index">
            <span v-if="column.customHeaderConfig">
              <component
                :is="column.customHeaderConfig.component"
                :column="column"
                v-bind="column.customHeaderConfig.attrs"
                v-on="column.customHeaderConfig.listeners"
              />
            </span>
            <span v-else><i v-if="column.required" class="required-icon">*</i>{{ column.label }}</span>
          </slot>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="showFooter" ref="baseTableFooter" class="base-table__footer">
      <slot name="total" />
      <slot name="pagination">
        <el-pagination
          v-if="tableList.length"
          :current-page="tablePagesProps.current"
          :page-size="tablePagesProps.size"
          :page-sizes="pageSizes"
          background
          :layout="pagesLayout"
          :total="tableTotal"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </slot>
    </div>
  </div>
</template>

<script>
import * as api from '@/api/common'
import { isFunction, throttle } from 'lodash'
export default {
  name: 'BaseTable',
  components: {},
  props: {
    tableId: {
      type: String,
      default: 'baseTableId'
    },
    title: {
      type: String,
      default: ''
    },
    columns: {
      type: Array,
      default: () => []
    },
    rowKey: {
      type: String,
      default: 'id'
    },
    height: {
      type: [Number, String],
      default: null
    },
    showIndexColumn: {
      type: Boolean,
      default: true
    },
    showSelectColumn: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    diff: { // 底部的默认间距
      type: [Number, String],
      default: 0
    },
    api: {
      type: String,
      default: ''
    },
    params: {
      type: Object,
      default: () => ({})
    },
    pages: {
      type: Object,
      default: () => ({})
    },
    pageSizes: {
      type: Array,
      default: () => [10, 20, 30, 50, 100]
    },
    pagesLayout: {
      type: String,
      default: 'total, sizes, prev, pager, next, jumper'
    },
    dataFromOut: { // 是否从外部引入数据，如果为true，则不请求数据
      type: Boolean,
      default: false
    },
    tableData: { // 从外部传入的数据
      type: Array,
      default: () => []
    },
    resultTransformFn: {
      type: Function,
      default: (result) => result
    },
    isOutRequestData: { // 是否外部执行请求列表数据
      type: Boolean,
      default: false
    },
    showEmpty: {
      type: Boolean,
      default: true
    },
    tableKey: {
      type: String,
      default: ''
    },
    direction: { // 表头的排列方式 vertical | horizontal
      type: String,
      default: 'horizontal'
    },
    totalApi: { // 单独请求列表总数配置
      type: String,
      default: ''
    }
  },
  data() {
    return {
      apiTableList: [],
      loading: false,
      columnList: [], // 表头显示的column
      tableHeight: 0, // 表格高度

      tableTotal: 0,
      tablePages: {
        current: 1,
        size: 10
      },

      isPagesHandler: false // 是否是分页触发
    }
  },
  computed: {
    tableList: {
      get() {
        if (this.dataFromOut) {
          return this.tableData
        }
        return this.apiTableList
      },
      set(val) {
        this.apiTableList = val
      }
    },
    tablePagesProps() {
      return Object.assign({}, { ...this.tablePages }, { ...this.pages })
    },
    requestParams() {
      if (this.showFooter) {
        return Object.assign({}, {
          page: this.tablePagesProps.current,
          limit: this.tablePagesProps.size
        }, this.params)
      }
      return this.params
    },
    fixedIndex() {
      const result = [this.showIndexColumn, this.showSelectColumn].filter(Boolean)
      return result.length
    },
    tableHeaderDirection() {
      return [`base-table__header--${this.direction}`]
    }
  },
  watch: {
    columns: {
      handler(val) {
        // console.log(JSON.parse(JSON.stringify(val)), 'watch')
        this.columnList = []
        this.$nextTick(() => {
          this.columnList = [...val]
        })
      },
      deep: true,
      immediate: true
    },
    tableList: {
      handler(val, oVal) {
        if (val.length > 0 && this.isOutRequestData) {
          this.$nextTick(() => {
            this.computedTableHeight()
          })
        }
        if (oVal && oVal.length === 0 && val.length > 0) {
          this.$nextTick(() => {
            this.computedTableHeight()
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  async mounted() {
    if (!this.dataFromOut && !this.isOutRequestData) {
      await this.getTableList()
    }
    this.throttle = throttle(() => {
      this.computedTableHeight()
    }, 300)
    window.addEventListener('resize', this.throttle)
    this.computedTableHeight()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.throttle)
  },
  activated() {
    window.addEventListener('resize', this.throttle)
    this.computedTableHeight()
  },
  deactivated() {
    window.removeEventListener('resize', this.throttle)
  },
  methods: {
    async getTableList() {
      if (!this.api) return

      this.loading = true

      try {
        // 并行请求列表和 count（如果配置了 totalApi）
        const listRequest = api.getAction({
          url: this.api,
          params: this.requestParams
        })

        const countRequest = this.totalApi && !this.isPagesHandler
          ? api.getAction({
            url: this.totalApi,
            params: this.requestParams
          }).catch(e => {
            console.warn('分页总数请求失败：', e)
            return null // 返回 null 不中断主流程
          })
          : null

        // 等待列表请求完成
        const { isSuccess, data } = await listRequest
        this.loading = false

        if (isSuccess && data.list?.length > 0) {
          this.tableList = isFunction(this.resultTransformFn)
            ? this.resultTransformFn(data.list)
            : data.list
        } else {
          this.tableList = []
        }

        // 获取 count
        if (!this.isPagesHandler) {
          if (countRequest) {
            const countRes = await countRequest
            if (countRes?.isSuccess) {
              this.tableTotal = countRes.data.count
            } else {
              // totalApi 请求失败或返回不成功，fallback 用主接口的 count（可能为 0）
              this.tableTotal = data.count || 0
            }
          } else {
            this.tableTotal = data.count || 0
          }
        }

        this.$emit('finish', this.tableList, data)
      } catch (e) {
        this.loading = false
        console.error('列表接口请求失败:', e)
        this.tableList = []
        this.tableTotal = 0
      } finally {
        // 无论分页与否，请求结束后都重置状态
        this.isPagesHandler = false
      }
    },
    computedTableHeight() {
      const bodyHeight = document.documentElement.clientHeight
      let footer = 0
      if (this.$refs.baseTableFooter) {
        // 30 是底部内容的margin-top
        footer = this.$refs.baseTableFooter.offsetHeight + 30
      }
      this.$nextTick(() => {
        // 会比顶部的tabs先渲染，所以需要用nextTick，要不然top会没有计算tabs的高度
        const top = document.getElementById(this.tableId).getBoundingClientRect().top
        this.tableHeight = bodyHeight - top - footer - this.diff
      })
    },
    handleSizeChange(size) {
      this.isPagesHandler = true
      this.$emit('pages-change', {
        current: this.tablePagesProps.current,
        size: size,
        method: 'size'
      })
      this.$nextTick(() => {
        this.getTableList()
      })
    },
    handleCurrentChange(page) {
      this.isPagesHandler = true
      this.$emit('pages-change', {
        current: page,
        size: this.tablePagesProps.size,
        method: 'current'
      })
      this.$nextTick(() => {
        this.getTableList()
      })
    },
    renderHeader(h, { column, $index }) {
      console.log("%c $index", "background-color: #2196f3; padding: 3px 6px; border-radius: 2px; font-size: 12px; color: #fff;", $index);
      const span = document.createElement('span')
      span.innerText = column.label
      document.body.appendChild(span)
      // 重点：获取 span 最小宽度，设置当前列，注意这里加了 20，字段较多时还是有挤压，且渲染后的 div 内左右 padding 都是 10，所以 +20 。（可能还有边距/边框等值，需要根据实际情况加上）
      column.minWidth = span.getBoundingClientRect().width + 60
      document.body.removeChild(span)
      return h('span', column.label)
    },
    customRenderIndex(index) {
      if (!this.showFooter && !this.pages.current) {
        return index + 1
      }
      const number = `${(this.pages.current - 1) * this.pages.size + index + 1}`
      return number
      // return number < 100 ? `0${number}`.slice(-2) : number
    },
    doTranslate(value, fn) {
      if (isFunction(fn)) {
        return fn(value)
      } else {
        return value
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.base-table {
  flex: 1;
  background: #fff;
  overflow: hidden;
  &__header {
    display: flex;
    margin-bottom: 10px;
    flex-shrink: 0;
    &-title {
      font-size: 16px;
      color: #303133;
    }
    &-operate {
      display: flex;
      align-items: center;
      margin-left: auto;
    }
    ::v-deep .el-form-item {
      margin-bottom: 0;
    }
  }
  &__header--horizontal {
    align-items: center;
    justify-content: space-between;
  }
  &__header--vertical {
    flex-direction: column;
  }
  .el-table {
    .required-icon {
      margin: 0 4px;
      font-size: 14px;
      color: #ff4949;
    }
  }
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 30px;
    .el-pagination {
      margin-left: auto;
    }
    &-total {
      display: flex;
      align-items: center;
      margin-left: auto;
      font-size: 14px;
      color: #061e26;
      .number {
        margin-right: 10px;
        font-weight: 600;
      }
    }
  }
}
</style>
